import React from "react";
// ANT DESIGN COMPONENTS
import { notification } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// VERIFICATION
import { ActionOutcome, describeOutcome } from "./actionVerification";

/**
 * The ONE place an action outcome becomes something an operator can see.
 *
 * Seven button components each grew their own `launchSuccessMessage`, and every
 * one of them showed `faCheckCircle` in green the instant the click handler
 * dispatched — before anything had been transmitted, let alone acted on. Seven
 * copies also meant seven chances to drift, and they already had: some warned on
 * timeout, some did not, and the theme class differed between them.
 *
 * Everything visible here comes from `describeOutcome`, so the four outcomes
 * cannot silently collapse into one another: the glyph, the wording and the
 * antd severity are all read from that table rather than chosen at the call
 * site. A new outcome is a row there, not a new notification body.
 *
 * The colour rides on the ICON rather than on the heading. `message` and
 * `description` stay plain strings, which is what every caller and every test
 * of the refusal path already reads, and the glyph is the element that has to
 * be recognisable at a glance anyway. Colour is therefore never the only
 * signal — the glyph and the wording differ across all four, which is what
 * keeps them separable in greyscale.
 */
export interface ActionOutcomeNotice {
  outcome: ActionOutcome;
  /**
   * The action's operator-facing label, prefixed to the description so a screen
   * with a dozen buttons says WHICH one this is about. Omitted by callers that
   * have no action — the connection-mode and bank-selection refusals.
   */
  label?: string;
  /**
   * Heading override. Defaults to the outcome's own title.
   * `notifyCommandBlocked` passes the specific refusal ("No bank selected")
   * because naming the cause beats repeating the category.
   */
  title?: string;
  /** Sentence override, same reasoning as `title`. */
  detail?: string;
  theme?: string;
  /**
   * The operator's per-command notification preference
   * (`userLogged.commandNotifications`). Defaults to true for callers that
   * deliberately ignore it.
   */
  commandNotifications?: boolean;
}

/**
 * Outcomes the preference is allowed to silence.
 *
 * `Sent` and `Confirmed` are routine per-command chatter — exactly what the
 * preference exists to turn off. `Blocked` and `Unconfirmed` are not: one means
 * the click did nothing at all, the other means the controller never reported
 * what the action said to expect. Hiding those behind a preference would
 * restore the silent failure this whole path exists to end, which is the same
 * reasoning `notifyCommandBlocked` has always documented.
 */
const SILENCEABLE: ReadonlySet<ActionOutcome> = new Set([
  ActionOutcome.Sent,
  ActionOutcome.Confirmed,
]);

/** Long enough to read the ones that need reading. */
const DWELL_SECONDS = 10;

/**
 * Show one outcome. Returns whether anything was rendered, so a caller (or a
 * test) can tell "suppressed by preference" from "shown".
 */
export default function notifyActionOutcome({
  outcome,
  label,
  title,
  detail,
  theme,
  commandNotifications = true,
}: ActionOutcomeNotice): boolean {
  if (!commandNotifications && SILENCEABLE.has(outcome)) return false;

  const descriptor = describeOutcome(outcome);
  const body = detail ?? descriptor.detail;
  notification[descriptor.severity]({
    message: title ?? descriptor.title,
    description: label ? `${label} — ${body}` : body,
    placement: "top",
    ...(SILENCEABLE.has(outcome) ? {} : { duration: DWELL_SECONDS }),
    ...(theme ? { className: `monitoring__notification__${theme}` } : {}),
    icon: (
      <FontAwesomeIcon
        icon={descriptor.icon}
        fontSize={18}
        className={`monitoring__modalSettings__icon ${descriptor.className}`}
      />
    ),
  });
  return true;
}
